import { unwrapResult } from '@reduxjs/toolkit';
import { useParams } from 'react-router';
import { slices, useAppDispatch } from 'src/redux';
import { useSnackBar } from '../use-snackbar';
import { useCallback, useState } from 'react';
import {
  GetTransactionDetailResponse,
  GetTransactionsViaTransactionNoResponse
} from 'src/types';
import { isEmpty } from 'lodash';
import { onTapTapCustomerTransactionInfoChangeViaInternal } from 'src/redux/slices/taptap-customer';

const { actions: transactionActions } = slices.transaction;
// const { actions: transactionPaymentActions } = slices.transactionPayments;
// const { actions: rmaActions } = slices.rma;

export const useTransaction = () => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const { transaction_no } = useParams();
  // const data = JSON.parse(sessionStorage.getItem('transactionData') || '');

  const [transactionId, setTransactionId] = useState<number>();
  const [transactionDetailsResponse, setTransactionDetailsResponse] = useState<
    GetTransactionDetailResponse | undefined
  >();
  const [transactionInfo, setTransactionInfo] = useState<
    GetTransactionsViaTransactionNoResponse
  >();
  const [failedChangeSerial, setFailedChangeSerial] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getTransactionDetails = useCallback(
    async (trxNo?: string) => {
      try {
        const finalTrxNo = trxNo || transaction_no;
        if (!finalTrxNo) return;

        const response = unwrapResult(
          await dispatch(
            transactionActions.getTransactionDetailThunk(finalTrxNo)
          )
        );
        if (response?.success && !isEmpty(response?.originalData)) {
          setTransactionDetailsResponse(response?.originalData);
          setTransactionId(response?.originalData?.data?.id);
        }
      } catch (error) {
        console.error('Error fetching transaction details:', error);
      }
    },
    [dispatch, transaction_no]
  );

  const onTransactionDetailsUpdate = async (
    newData: GetTransactionDetailResponse
  ) => {
    try {
      const response = unwrapResult(
        await dispatch(
          transactionActions.updateTransactionDetailThunk({
            ...newData.data,
            purchase_category: newData?.data?.purchase_category,
            type_of_shipping: newData?.data?.type_of_shipping,
            build_type: newData?.data?.build_type,
            type_of_sales_transaction: newData?.data?.type_of_sales_transaction,
            business_name: newData?.data?.business_name,
            business_address: newData?.data?.business_address,
            tin: newData?.data?.tin,
            terms: newData?.data?.terms
          })
        )
      );

      if (response?.success && !isEmpty(response?.originalData)) {
        snackBar.show({
          severity: 'success',
          message: 'transaction remarks update success'
        });

        getTransactionDetails();
        return;
      }
      if (!response?.success && response?.errors) {
        snackBar.show({
          severity: 'error',
          message: `${JSON.stringify(response?.errors || '')}`
        });
        return;
      }
      snackBar.show({
        severity: 'error',
        message: 'transaction details update failed'
      });
    } catch (error) {
      console.error('Error updating transaction details:', error);
    }
  };

  const getTransactionInfo = useCallback(
    // reason for for updating taptap customer
    async (
      reason?: 'voided_item' | 'added_item' | 'swap_sn' | 'srp_percent_change'
    ) => {
      try {
        setIsLoading(true);
        const response = unwrapResult(
          await dispatch(
            transactionActions.getTransactionViaTransactionNo(transaction_no)
          ).finally(() => setIsLoading(false))
        );
        if (response?.success && !isEmpty(response?.originalData)) {
          setFailedChangeSerial(false);
          setTransactionInfo({ ...response?.originalData, id: transactionId });
          if (reason) {
            onTapTapCustomerTransactionInfoChangeViaInternal(
              response?.originalData
            );
          }
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching transaction info:', error);
      }
    },
    [dispatch, transactionId, transaction_no]
  );

  return {
    setIsLoading,
    setTransactionInfo,
    setFailedChangeSerial,
    setTransactionDetailsResponse,

    transactionInfo,
    failedChangeSerial,
    isLoading,
    getTransactionInfo,
    transactionDetailsResponse,
    getTransactionDetails,
    onTransactionDetailsUpdate
  };
};
